<template>
  <div class="portal-overlay" @click.self="oth_showinsuPortal()">
    <div class="portal-dialog">

    <!-- 헤더 -->
    <div class="portal-header">
      <div class="header-row">
        <button class="nav-btn" v-if="step > 1" @click="goBack">
          <i class="ph ph-caret-left"></i>
        </button>
        <h1 v-if="step === 1">보험 포탈</h1>
        <h1 v-else></h1>
        <button class="nav-btn close" @click="oth_showinsuPortal()">
          <i class="ph ph-x"></i>
        </button>
      </div>

      <!-- 검색 + 탭 (1단계만) -->
      <template v-if="step === 1">
        <div class="search-bar">
          <i class="ph ph-magnifying-glass"></i>
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="보험사 검색 (옛이름도 가능)"
            ref="searchInput"
          />
          <button class="search-clear" v-if="searchQuery" @click="searchQuery = ''">
            <i class="ph ph-x-circle-fill"></i>
          </button>
        </div>
        <div class="tab-bar">
          <button 
            v-for="tab in companyTabs" 
            :key="tab.value"
            :class="{ active: activeTab === tab.value }"
            @click="activeTab = tab.value"
          >
            {{ tab.label }}
            <span class="count">{{ getTabCount(tab.value) }}</span>
          </button>
        </div>
      </template>
    </div>

    <!-- ========== STEP 1: 회사 찾기 ========== -->
    <transition :name="transitionName" mode="out-in">
      <div class="portal-body" v-if="step === 1" key="step1">
        <div class="company-grid">
          <div 
            class="company-card"
            v-for="company in filteredCompanies"
            :key="company.idx"
            @click="selectCompany(company)"
          >
            <div class="card-account-badge" v-if="getAccount(company.idx)">
              <i class="ph-fill ph-check-circle"></i>
            </div>
            <div class="card-img">
              <img :src="company.short_img" :alt="company.name" />
            </div>
            <span class="card-name">{{ company.name }}</span>
          </div>

          <div class="empty" v-if="filteredCompanies.length === 0">
            <i class="ph ph-magnifying-glass"></i>
            <p>검색 결과가 없습니다</p>
          </div>
        </div>

        <!-- 기관 바로가기 -->
        <div class="org-section">
          <div class="org-title">
            <i class="ph ph-bank"></i> 기관 바로가기
          </div>
          <div class="org-list">
            <a 
              class="org-item"
              v-for="(org, i) in extraShortcut" 
              :key="i"
              :href="org.link"
              target="_blank"
              rel="noopener"
            >
              <div class="org-img">
                <img :src="org.img" :alt="org.name" />
              </div>
              <span>{{ org.name }}</span>
            </a>
          </div>
        </div>
      </div>

      <!-- ========== STEP 2: 올인원 상세 ========== -->
      <div class="portal-body" v-else-if="step === 2" key="step2">

        <!-- 회사 히어로 -->
        <div class="company-hero">
          <div class="hero-logo">
            <img :src="selectedCompany?.short_img" :alt="selectedCompany?.name" />
          </div>
          <div class="hero-info">
            <h2>{{ selectedCompany?.name }}</h2>
            <div class="hero-meta">
              <span class="type-badge">{{ getTypeLabel(selectedCompany?.type) }}</span>
              <span 
                class="hero-phone" 
                v-if="selectedCompany?.phone"
                @click="callPhone(selectedCompany.phone)"
              >
                <i class="ph ph-phone"></i> {{ selectedCompany.phone }}
              </span>
            </div>
          </div>
        </div>

        <!-- 포탈 접속 버튼 -->
        <a 
          class="portal-go-btn"
          v-if="selectedCompany?.system_url"
          :href="selectedCompany.system_url"
          target="_blank"
          rel="noopener"
        >
          <div class="portal-go-icon">
            <i class="ph ph-sign-in"></i>
          </div>
          <div class="portal-go-text">
            <strong>영업포탈 열기</strong>
            <span>{{ extractDomain(selectedCompany.system_url) }}</span>
          </div>
          <i class="ph ph-arrow-square-out"></i>
        </a>

        <!-- 계정 섹션 -->
        <div class="section" :class="{ open: openSection === 'account' }">
          <div class="section-header" @click="openSection = openSection === 'account' ? '' : 'account'">
            <div class="section-icon account">
              <i class="ph ph-key"></i>
            </div>
            <div class="section-info">
              <strong>계정</strong>
              <span v-if="currentAccount" class="section-sub has">{{ currentAccount.identify }}</span>
              <span v-else class="section-sub none">미등록</span>
            </div>
            <div class="section-actions">
              <button 
                class="quick-copy" 
                v-if="currentAccount" 
                @click.stop="copyText(currentAccount.identify, 'ID')"
              >ID</button>
              <button 
                class="quick-copy" 
                v-if="currentAccount" 
                @click.stop="copyText(currentAccount.password, 'PW')"
              >PW</button>
              <i class="ph chevron" :class="openSection === 'account' ? 'ph-caret-up' : 'ph-caret-down'"></i>
            </div>
          </div>

          <div class="section-body" v-show="openSection === 'account'">
            <div class="account-edit">
              <div class="field">
                <label>아이디 (FP코드)</label>
                <div class="field-row">
                  <input type="text" v-model="form.identify" placeholder="로그인 아이디" />
                  <button class="field-btn" v-if="form.identify" @click="copyText(form.identify, '아이디')">
                    <i class="ph ph-copy"></i>
                  </button>
                </div>
              </div>

              <div class="field">
                <label>비밀번호</label>
                <div class="field-row">
                  <input 
                    :type="showPassword ? 'text' : 'password'" 
                    v-model="form.password" 
                    placeholder="비밀번호" 
                  />
                  <button class="field-btn" @click="showPassword = !showPassword">
                    <i class="ph" :class="showPassword ? 'ph-eye-slash' : 'ph-eye'"></i>
                  </button>
                  <button class="field-btn" v-if="form.password" @click="copyText(form.password, '비밀번호')">
                    <i class="ph ph-copy"></i>
                  </button>
                </div>
              </div>

              <div class="field">
                <label>메모</label>
                <input type="text" v-model="form.memo" placeholder="예: 90일마다 변경, OTP 필요" />
              </div>

              <div class="pw-age-bar" v-if="currentAccount">
                <div class="pw-age-info">
                  <i class="ph ph-clock"></i>
                  PW 변경 
                  <strong :class="getPasswordAgeClass(currentAccount.password_changed_at)">
                    {{ getPasswordAgeDays(currentAccount.password_changed_at) }}일
                  </strong>
                  경과
                </div>
                <button class="pw-reset" @click="markPasswordChanged">
                  <i class="ph ph-arrows-clockwise"></i> 갱신
                </button>
              </div>

              <div class="account-btns">
                <button class="btn-del" v-if="currentAccount" @click="deleteAccount">
                  <i class="ph ph-trash"></i> 삭제
                </button>
                <button class="btn-save" @click="saveAccount">
                  <i class="ph ph-floppy-disk"></i> {{ currentAccount ? '저장' : '등록' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 청구 섹션 -->
        <div class="section" :class="{ open: openSection === 'claim' }" v-if="claimData.length > 0">
          <div class="section-header" @click="openSection = openSection === 'claim' ? '' : 'claim'">
            <div class="section-icon claim">
              <i class="ph ph-file-text"></i>
            </div>
            <div class="section-info">
              <strong>청구</strong>
              <span class="section-sub has">
                {{ claimData.map(c => c.type).filter((v,i,a) => a.indexOf(v)===i).join(' · ') }}
              </span>
            </div>
            <div class="section-actions">
              <span class="section-count">{{ claimData.length }}</span>
              <i class="ph chevron" :class="openSection === 'claim' ? 'ph-caret-up' : 'ph-caret-down'"></i>
            </div>
          </div>

          <div class="section-body" v-show="openSection === 'claim'">
            <div class="detail-list">
              <div class="detail-card" v-for="item in claimData" :key="item.idx">
                <div class="detail-left">
                  <span class="detail-type">
                    <i :class="getClaimIcon(item.type)"></i>
                    {{ item.type }}
                  </span>
                  <span class="detail-label" v-if="item.label">{{ item.label }}</span>
                </div>
                <div class="detail-right">
                  <span class="detail-value">{{ item.value }}</span>
                  <button 
                    class="detail-action" 
                    v-if="item.type === '전화'" 
                    @click="callPhone(item.value)"
                  >
                    <i class="ph ph-phone-call"></i>
                  </button>
                  <button class="detail-action" @click="copyText(item.value, item.type)">
                    <i class="ph ph-copy"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 연락처 섹션 -->
        <div class="section" :class="{ open: openSection === 'contact' }" v-if="selectedCompany?.phone || selectedCompany?.fax">
          <div class="section-header" @click="openSection = openSection === 'contact' ? '' : 'contact'">
            <div class="section-icon contact">
              <i class="ph ph-phone"></i>
            </div>
            <div class="section-info">
              <strong>연락처</strong>
              <span class="section-sub has" v-if="selectedCompany?.phone">{{ selectedCompany.phone }}</span>
            </div>
            <div class="section-actions">
              <button 
                class="quick-call" 
                v-if="selectedCompany?.phone"
                @click.stop="callPhone(selectedCompany.phone)"
              >
                <i class="ph ph-phone-call"></i>
              </button>
              <i class="ph chevron" :class="openSection === 'contact' ? 'ph-caret-up' : 'ph-caret-down'"></i>
            </div>
          </div>

          <div class="section-body" v-show="openSection === 'contact'">
            <div class="detail-list">
              <div class="detail-card" v-if="selectedCompany?.phone">
                <div class="detail-left">
                  <span class="detail-type"><i class="ph ph-phone"></i> 대표전화</span>
                </div>
                <div class="detail-right">
                  <span class="detail-value">{{ selectedCompany.phone }}</span>
                  <button class="detail-action" @click="callPhone(selectedCompany.phone)">
                    <i class="ph ph-phone-call"></i>
                  </button>
                  <button class="detail-action" @click="copyText(selectedCompany.phone, '대표전화')">
                    <i class="ph ph-copy"></i>
                  </button>
                </div>
              </div>

              <div class="detail-card" v-if="selectedCompany?.fax">
                <div class="detail-left">
                  <span class="detail-type"><i class="ph ph-printer"></i> 대표팩스</span>
                </div>
                <div class="detail-right">
                  <span class="detail-value">{{ selectedCompany.fax }}</span>
                  <button class="detail-action" @click="copyText(selectedCompany.fax, '팩스')">
                    <i class="ph ph-copy"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </transition>

    </div>
  </div>
</template>

<script>
import { ref, inject, onMounted, computed } from 'vue';
import { useBackButton } from '@/utils/useBackBtn';

export default {
  name: 'InsuPortal',
  props: {
    oth_showinsuPortal: { type: Function, required: true }
  },
  setup(props) {
    const getMbid = inject('getMbid');
    const sql_query = inject('sql_query');
    const insuCompanys = inject('insuCompanys', ref([]));

    const step = ref(1);
    const activeTab = ref('fire');
    const searchQuery = ref('');
    const searchInput = ref(null);
    const selectedCompany = ref(null);
    const accountList = ref([]);
    const claimData = ref([]);
    const showPassword = ref(false);
    const openSection = ref('');
    const transitionName = ref('slide-left');
    const form = ref({ identify: '', password: '', memo: '' });

    useBackButton(() => {
      if (step.value > 1) goBack();
      else props.oth_showinsuPortal();
    });

    const companyTabs = [
      { label: '손해', value: 'fire' },
      { label: '생명', value: 'life' },
      { label: '기타', value: 'other' },
    ];

    const extraShortcut = [
      { name: 'AMA다이어리', link: 'https://amadiary.com', img: 'https://amadiary.com/pub/acon/ama_logo_bg.png' },
      { name: '탐스2', link: 'https://ktams.kgaasset.com/Install/newLauncher_XP.html', img: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSPNsT-vWXhvoaMl-N1ZGpjZmKXlCj3oQ78AQ&s' },
      { name: '모홈', link: 'https://www.mohom.kr/', img: 'https://cdn.litt.ly/images/6EOhVOPX4lCq3kgkBbkVIovtbNw238PZ?s=180x180&f=webp' },
      { name: '정부24', link: 'https://plus.gov.kr/', img: 'https://play-lh.googleusercontent.com/9ZwlSxrZ5zAGydEtON0V6SkvezO_v6xIlbQEIYR7AKPiJ7NLoYow-2LYjmRH48FC1b3CXBicR4DJw3zZ-GXi' },
      { name: '대법원', link: 'https://www.scourt.go.kr/scourt/index.html', img: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRLLeAL3Vd9nzMdUR1bYn8c6vGNawjWUkY6dg&s' },
      { name: '보험연수원', link: 'https://www.in.or.kr/', img: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ_XY3ix2gGJaqNatDNchSgIDw4GiamKZwelg&s' },
      { name: '생보협회', link: 'https://www.klia.or.kr/', img: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTn-aJSouIvG2-yL6m22NDqUZ-EfF8Kw3gwtw&s' },
      { name: '손보협회', link: 'https://www.knia.or.kr/', img: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQNa09N7yNPymtGoISiXJpI1IERXw9d3IYXlA&s' },
      { name: '금감원', link: 'https://www.fss.or.kr/fss/main', img: 'https://cdn.smarttoday.co.kr/news/photo/202406/52141_45647_2216.jpg' },
    ];

    function getTabCount(type) {
      if (!insuCompanys?.value) return 0;
      return insuCompanys.value.filter(c => c.type === type).length;
    }

    function getTypeLabel(type) {
      return { fire: '손해', life: '생명', other: '기타' }[type] || type;
    }

    function extractDomain(url) {
      try { return new URL(url).hostname.replace('www.', ''); } catch { return ''; }
    }

    const filteredCompanies = computed(() => {
      if (!insuCompanys?.value) return [];
      return insuCompanys.value
        .filter(c => {
          if (c.type !== activeTab.value) return false;
          if (searchQuery.value) {
            const q = searchQuery.value.toLowerCase();
            const nameMatch = c.name.toLowerCase().includes(q);
            let oldMatch = false;
            if (c.old_name) {
              try {
                oldMatch = JSON.parse(c.old_name).some(n => n.toLowerCase().includes(q));
              } catch(e) { console.log(e) }
            }
            return nameMatch || oldMatch;
          }
          return true;
        })
        .sort((a, b) => a.sort_no - b.sort_no);
    });

    const currentAccount = computed(() => {
      if (!selectedCompany.value) return null;
      return getAccount(selectedCompany.value.idx);
    });

    function getAccount(companyIdx) {
      return accountList.value.find(a => a.company_idx == companyIdx);
    }

    async function fetchAccounts() {
      const res = await sql_query(`
        SELECT a.*, c.name AS company_name
        FROM insurance_account a
        LEFT JOIN insu_company c ON c.idx = a.company_idx
        WHERE a.mb_id = '${getMbid()}'
      `);
      accountList.value = Array.isArray(res) ? res : [];
    }

    async function fetchClaimData(companyCode) {
      const res = await sql_query(`
        SELECT * FROM insu_company_claim 
        WHERE company_code = '${companyCode}' ORDER BY idx ASC
      `);
      claimData.value = Array.isArray(res) ? res : [];
    }

    function selectCompany(company) {
      selectedCompany.value = company;
      transitionName.value = 'slide-left';
      openSection.value = '';
      step.value = 2;
      fetchClaimData(company.code);
      const account = getAccount(company.idx);
      form.value = account
        ? { identify: account.identify, password: account.password, memo: account.memo || '' }
        : { identify: '', password: '', memo: '' };
    }

    function goBack() {
      transitionName.value = 'slide-right';
      selectedCompany.value = null;
      openSection.value = '';
      step.value = 1;
    }

    async function saveAccount() {
      if (!form.value.identify || !form.value.password) {
        alert('아이디와 비밀번호를 입력해주세요.'); return;
      }
      const account = currentAccount.value;
      const pw = form.value.password.replace(/'/g, "\\'");
      const memo = form.value.memo ? `'${form.value.memo.replace(/'/g, "\\'")}'` : 'NULL';
      if (account) {
        const pwChanged = form.value.password !== account.password;
        await sql_query(`UPDATE insurance_account SET identify='${form.value.identify}', password='${pw}', memo=${memo} ${pwChanged ? ', password_changed_at=NOW()' : ''} WHERE idx=${account.idx}`);
      } else {
        await sql_query(`INSERT INTO insurance_account (mb_id, company_idx, identify, password, memo, password_changed_at) VALUES ('${getMbid()}', ${selectedCompany.value.idx}, '${form.value.identify}', '${pw}', ${memo}, NOW())`);
      }
      await fetchAccounts();
      alert('저장되었습니다.');
    }

    async function deleteAccount() {
      if (!currentAccount.value) return;
      if (!confirm(`${selectedCompany.value.name} 계정을 삭제하시겠습니까?`)) return;
      await sql_query(`DELETE FROM insurance_account WHERE idx=${currentAccount.value.idx}`);
      await fetchAccounts();
      form.value = { identify: '', password: '', memo: '' };
    }

    async function markPasswordChanged() {
      if (!currentAccount.value) return;
      if (!confirm('비밀번호 변경일을 오늘로 갱신하시겠습니까?')) return;
      await sql_query(`UPDATE insurance_account SET password_changed_at=NOW() WHERE idx=${currentAccount.value.idx}`);
      await fetchAccounts();
    }

    function getPasswordAgeDays(date) {
      if (!date) return '?';
      return Math.floor((new Date() - new Date(date)) / 86400000);
    }

    function getPasswordAgeClass(date) {
      const d = getPasswordAgeDays(date);
      if (d === '?') return 'unknown';
      if (d >= 90) return 'danger';
      if (d >= 60) return 'warning';
      return 'safe';
    }

    function getClaimIcon(type) {
      return { '전화': 'ph ph-phone', '팩스': 'ph ph-printer', '온라인': 'ph ph-globe' }[type] || 'ph ph-info';
    }

    function copyText(text, label) {
      if (!text) return;
      navigator.clipboard.writeText(text);
      alert(`${label}가 복사되었습니다.`);
    }

    function callPhone(phone) {
      window.location.href = `tel:${phone}`;
    }

    onMounted(() => { fetchAccounts(); });

    return {
      step, activeTab, searchQuery, searchInput,
      selectedCompany, openSection,
      companyTabs, extraShortcut, filteredCompanies,
      claimData, accountList, currentAccount,
      form, showPassword, transitionName,
      getTabCount, getTypeLabel, getAccount, extractDomain,
      selectCompany, goBack,
      saveAccount, deleteAccount, markPasswordChanged,
      getPasswordAgeDays, getPasswordAgeClass,
      getClaimIcon, copyText, callPhone
    };
  }
}
</script>

<style scoped>
/* ===== 오버레이 ===== */
.portal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9998;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ===== 다이얼로그 ===== */
.portal-dialog {
  width: 92%;
  max-width: 520px;
  max-height: 85vh;
  background: var(--color-background);
  border-radius: 20px;
  border: 1px solid var(--color-background-light);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

/* ===== 헤더 ===== */
.portal-header {
  flex-shrink: 0;
  padding: 12px 16px 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: var(--color-background);
  z-index: 10;
}

.header-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
}

.header-row h1 {
  flex: 1;
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: 700;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: var(--color-background-light);
  color: var(--color-text-secondary);
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
}

.nav-btn:active { transform: scale(0.92); }

/* 검색 */
.search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: var(--color-background-light);
  border-radius: 12px;
  border: 1.5px solid transparent;
  transition: all 0.2s;
}

.search-bar:focus-within {
  border-color: var(--color-brand);
  background: var(--color-background);
}

.search-bar > i { color: var(--color-text-secondary); font-size: 18px; flex-shrink: 0; }

.search-bar input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  min-width: 0;
}

.search-clear {
  border: none;
  background: none;
  color: var(--color-text-secondary);
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  display: flex;
}

/* 탭 */
.tab-bar {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid var(--color-background-light);
}

.tab-bar button {
  flex: 1;
  padding: 10px 4px;
  border: none;
  background: transparent;
  font-size: var(--font-size-sm);
  font-weight: 500;
  color: var(--color-text-secondary);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.tab-bar button.active {
  color: var(--color-text-primary);
  border-bottom-color: var(--color-brand);
  font-weight: 600;
}

.tab-bar .count {
  font-size: 11px;
  background: var(--color-background-light);
  padding: 1px 6px;
  border-radius: 8px;
  font-weight: 600;
}

.tab-bar button.active .count {
  background: var(--color-brand);
  color: #000;
}

/* ===== 바디 ===== */
.portal-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  scrollbar-width: none;
}

.portal-body::-webkit-scrollbar { display: none; }

/* ===== STEP1: 회사 그리드 ===== */
.company-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

.company-card {
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--color-background-light);
  border-radius: 12px;
  cursor: pointer;
  position: relative;
  transition: all 0.15s;
}

.company-card:active { transform: scale(0.95); }

.card-account-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  font-size: 15px;
  color: #4CAF50;
}

.card-img {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-img img { width: 100%; height: 100%; object-fit: contain; }

.card-name {
  font-size: var(--font-size-xs);
  font-weight: 500;
  color: var(--color-text-primary);
  text-align: center;
  max-width: 90%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empty {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 20px;
  color: var(--color-text-secondary);
  gap: 8px;
}
.empty i { font-size: 36px; }

/* 기관 */
.org-section {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--color-background-light);
}

.org-title {
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
}

.org-list {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.org-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 8px;
  background: var(--color-background-light);
  border-radius: 10px;
  text-decoration: none;
  color: var(--color-text-primary);
  transition: all 0.15s;
}

.org-item:active { transform: scale(0.95); }

.org-img {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.org-img img { width: 100%; height: 100%; object-fit: contain; }
.org-item span { font-size: 11px; font-weight: 500; text-align: center; line-height: 1.2; }

/* ===== STEP2: 올인원 상세 ===== */

/* 회사 히어로 */
.company-hero {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: var(--color-background-light);
  border-radius: 16px;
  margin-bottom: 12px;
}

.hero-logo {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.hero-logo img { width: 100%; height: 100%; object-fit: contain; }

.hero-info { flex: 1; min-width: 0; }

.hero-info h2 {
  margin: 0 0 6px;
  font-size: var(--font-size-lg);
  font-weight: 700;
  color: var(--color-text-primary);
}

.hero-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.type-badge {
  font-size: var(--font-size-xs);
  font-weight: 600;
  background: rgba(255,255,255,0.08);
  padding: 2px 8px;
  border-radius: 6px;
  color: var(--color-text-secondary);
}

.hero-phone {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  transition: color 0.15s;
}

.hero-phone:active { color: var(--color-brand); }

/* 포탈 버튼 */
.portal-go-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: var(--color-brand);
  color: #000;
  border-radius: 14px;
  text-decoration: none;
  transition: all 0.15s;
  margin-bottom: 12px;
}

.portal-go-btn:active { transform: scale(0.97); }

.portal-go-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.portal-go-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.portal-go-text strong { font-size: var(--font-size-md); }
.portal-go-text span { font-size: var(--font-size-xs); opacity: 0.7; }

.portal-go-btn > i:last-child { font-size: 18px; opacity: 0.6; }

/* ===== 아코디언 섹션 ===== */
.section {
  background: var(--color-background-light);
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 8px;
  border: 1.5px solid transparent;
  transition: border-color 0.2s;
}

.section.open {
  border-color: rgba(255,255,255,0.06);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;
}

.section-header:active { background: rgba(255,255,255,0.02); }

.section-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 19px;
  flex-shrink: 0;
}

.section-icon.account { background: #F3E5F5; color: #7B1FA2; }
.section-icon.claim   { background: #FFF3E0; color: #E65100; }
.section-icon.contact  { background: #E8F5E9; color: #2E7D32; }

.section-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.section-info strong {
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
}

.section-sub {
  font-size: var(--font-size-xs);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.section-sub.has {
  color: var(--color-text-secondary);
  font-family: monospace;
}

.section-sub.none {
  color: var(--color-text-secondary);
  opacity: 0.5;
}

.section-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.quick-copy {
  border: none;
  background: var(--color-background);
  color: var(--color-text-secondary);
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
  letter-spacing: 0.5px;
}

.quick-copy:active { background: var(--color-brand); color: #000; }

.quick-call {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: #E8F5E9;
  color: #2E7D32;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  transition: all 0.15s;
}

.quick-call:active { transform: scale(0.9); }

.section-count {
  font-size: 11px;
  font-weight: 700;
  background: var(--color-background);
  padding: 2px 8px;
  border-radius: 8px;
  color: var(--color-text-secondary);
}

.chevron {
  font-size: 16px;
  color: var(--color-text-secondary);
  transition: transform 0.2s;
}

.section-body {
  padding: 0 14px 14px;
}

/* ===== 상세 리스트 ===== */
.detail-list { display: flex; flex-direction: column; gap: 6px; }

.detail-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 12px;
  background: var(--color-background);
  border-radius: 10px;
  gap: 12px;
}

.detail-left { display: flex; flex-direction: column; gap: 2px; min-width: 0; }

.detail-type {
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: 6px;
}

.detail-label { font-size: var(--font-size-xs); color: var(--color-text-secondary); }

.detail-right { display: flex; align-items: center; gap: 5px; flex-shrink: 0; }

.detail-value { font-size: var(--font-size-sm); color: var(--color-text-primary); font-weight: 500; }

.detail-action {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: var(--color-background-light);
  color: var(--color-text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  transition: all 0.15s;
}

.detail-action:active { transform: scale(0.9); background: var(--color-brand); color: #000; }

/* ===== 계정 편집 ===== */
.account-edit { display: flex; flex-direction: column; gap: 12px; }

.field label {
  font-size: var(--font-size-xs);
  font-weight: 600;
  color: var(--color-text-secondary);
  display: block;
  margin-bottom: 6px;
}

.field input {
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid var(--color-background);
  border-radius: 10px;
  font-size: var(--font-size-sm);
  background: var(--color-background);
  color: var(--color-text-primary);
  box-sizing: border-box;
  transition: all 0.15s;
}

.field input:focus { outline: none; border-color: var(--color-brand); }

.field-row { display: flex; gap: 6px; }
.field-row input { flex: 1; min-width: 0; }

.field-btn {
  width: 38px;
  flex-shrink: 0;
  border: 1.5px solid var(--color-background);
  border-radius: 10px;
  background: var(--color-background);
  color: var(--color-text-secondary);
  cursor: pointer;
  font-size: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.field-btn:active { background: var(--color-brand); color: #000; }

.pw-age-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: var(--color-background);
  border-radius: 10px;
  gap: 8px;
}

.pw-age-info {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  gap: 4px;
}

.pw-age-info strong.safe { color: #2e7d32; }
.pw-age-info strong.warning { color: #ef6c00; }
.pw-age-info strong.danger { color: #c62828; }

.pw-reset {
  border: none;
  background: none;
  font-size: var(--font-size-xs);
  color: var(--color-brand);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.account-btns { display: flex; gap: 8px; justify-content: flex-end; margin-top: 2px; }

.account-btns button {
  padding: 10px 18px;
  border: none;
  border-radius: 10px;
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s;
}

.account-btns button:active { transform: scale(0.96); }
.btn-del { background: #ffebee; color: #c62828; }
.btn-save { background: var(--color-brand); color: #000; }

/* ===== 슬라이드 트랜지션 ===== */
.slide-left-enter-active, .slide-left-leave-active,
.slide-right-enter-active, .slide-right-leave-active {
  transition: all 0.25s ease;
}

.slide-left-enter-from  { opacity: 0; transform: translateX(40px); }
.slide-left-leave-to    { opacity: 0; transform: translateX(-40px); }
.slide-right-enter-from { opacity: 0; transform: translateX(-40px); }
.slide-right-leave-to   { opacity: 0; transform: translateX(40px); }

/* ===== 태블릿 ===== */
@media (min-width: 601px) and (max-width: 1024px) {
  .portal-dialog { max-width: 560px; max-height: 80vh; }
  .company-grid { grid-template-columns: repeat(4, 1fr); }
  .org-list { grid-template-columns: repeat(4, 1fr); }
}

/* ===== 데스크탑 ===== */
@media (min-width: 1025px) {
  .portal-dialog { max-width: 540px; max-height: 78vh; }
  .company-grid { grid-template-columns: repeat(4, 1fr); }
  .org-list { grid-template-columns: repeat(4, 1fr); }
}

/* ===== 모바일 ===== */
@media (max-width: 600px) {
  .portal-overlay {
    align-items: flex-end;
  }

  .portal-dialog {
    width: 100%;
    max-width: 100%;
    max-height: 92vh;
    border-radius: 20px 20px 0 0;
    border: none;
    border-top: 1px solid var(--color-background-light);
    animation: sheetUp 0.3s ease;
  }

  @keyframes sheetUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }

  .portal-header { padding: 10px 14px 0; }
  .header-row h1 { font-size: var(--font-size-md); }
  .portal-body { padding: 14px; }
  .company-grid { grid-template-columns: repeat(3, 1fr); gap: 8px; }
  .card-img { width: 38px; height: 38px; }
  .card-name { font-size: 11px; }
  .org-list { grid-template-columns: repeat(3, 1fr); gap: 6px; }
  .org-img { width: 30px; height: 30px; }
  .org-item { padding: 10px 6px; }
  .org-item span { font-size: 10px; }

  .hero-logo { width: 48px; height: 48px; border-radius: 12px; }
  .hero-info h2 { font-size: var(--font-size-md); }
  .portal-go-icon { width: 36px; height: 36px; font-size: 18px; }

  .account-btns { flex-direction: column; }
  .account-btns button { justify-content: center; }
}

@media (max-width: 360px) {
  .card-img { width: 34px; height: 34px; }
  .card-name { font-size: 10px; }
}
</style>